import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, User, ArrowLeft, BookmarkX, Bookmark } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ReadingListPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    loadBookmarks();
  }, [user, authLoading, navigate]);

  const loadBookmarks = async () => {
    setLoading(true);
    try {
      const res = await api.get("/bookmarks");
      setArticles(res.data.articles || []);
    } catch { setArticles([]); }
    setLoading(false);
  };

  const removeBookmark = async (articleId) => {
    try {
      await api.delete(`/bookmarks/${articleId}`);
      setArticles(prev => prev.filter(a => a.id !== articleId));
      toast.success("Removed from reading list");
    } catch { toast.error("Failed to remove"); }
  };

  if (authLoading || loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Skeleton className="h-10 w-64 mb-8" />
        {[1,2,3].map(i => <Skeleton key={i} className="h-24 mb-4" />)}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="reading-list-page">
      <SEOHead title="My Reading List" />

      <Link to="/" className="inline-flex items-center gap-2 text-sm font-sans text-muted-foreground hover:text-foreground mb-6" data-testid="back-home">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </Link>

      <div className="flex items-center gap-3 mb-8">
        <Bookmark className="h-6 w-6 text-[#1E88E5]" />
        <h1 className="text-3xl sm:text-4xl font-serif font-bold text-foreground">My Reading List</h1>
      </div>

      {articles.length === 0 ? (
        <div className="text-center py-16 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <Bookmark className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-lg font-sans text-muted-foreground">Your reading list is empty</p>
          <p className="text-sm font-sans text-muted-foreground mt-2">Bookmark articles to read them later</p>
          <Link to="/">
            <Button className="mt-6 bg-[#1E88E5] hover:bg-[#1565C0] font-sans" data-testid="browse-articles">
              Browse Articles
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm font-sans text-muted-foreground mb-4">{articles.length} saved article{articles.length !== 1 ? "s" : ""}</p>
          {articles.map((art) => (
            <div key={art.id} className="flex gap-4 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 group" data-testid={`bookmark-item-${art.id}`}>
              {art.featured_image && (
                <Link to={`/article/${art.id}`} className="shrink-0">
                  <img src={art.featured_image} alt="" className="w-28 h-20 object-cover" loading="lazy" />
                </Link>
              )}
              <div className="flex-1 min-w-0">
                <Link to={`/article/${art.id}`} className="font-serif font-bold text-foreground hover:text-[#1E88E5] transition-colors line-clamp-2 text-lg">
                  {art.title}
                </Link>
                <p className="text-sm font-sans text-muted-foreground mt-1 line-clamp-1">{art.summary}</p>
                <div className="flex items-center gap-3 mt-2 text-xs font-sans text-muted-foreground">
                  {art.author_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{art.author_name}</span>}
                  {art.reading_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{art.reading_time}</span>}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => removeBookmark(art.id)}
                data-testid={`remove-bookmark-${art.id}`}
              >
                <BookmarkX className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
